import React, { useState, FC } from 'react';
import { Formik, Form } from 'formik';
import * as yup from 'yup';
import Auth from '@aws-amplify/auth';
import Router from 'next/router';
import {
  Alert,
  Button,
  Flex,
  FormControl,
  FormLabel,
  Input,
  LightMode,
  Stack,
  useColorModeValue as mode,
  FormErrorMessage,
  AlertIcon,
  InputGroup,
  InputRightElement,
  FormHelperText,
  UnorderedList,
  ListItem
} from '@chakra-ui/react';
import { passwordRegExp } from '../../libs/utils';
import { ForgotPassword } from '../../libs/authentication';
import useToggle from '../../hooks/useToggle';

type ResetPasswordFormProps = {
  user: {
    username: string;
  };
  // TODO:
  // eslint-disable-next-line no-unused-vars
  callback(err: string | null, password?: string): void;
};

const ResetPasswordForm: FC<ResetPasswordFormProps> = ({ callback, user }) => {
  const [error, setError] = useState('');
  const [showPassword, toggleShowPassword] = useToggle(false);
  const [verificationSent, setVerificationSent] = useState(false);

  const sentMessage = verificationSent && (
    <Alert status='success' mb='4' borderRadius='md'>
      {/*  eslint-disable-next-line react/jsx-one-expression-per-line */}
      An email with a verification code has been sent to {user.username}
    </Alert>
  );

  function onCancel() {
    setError('');

    callback(null);
  }

  async function onNotReceived() {
    setError('');

    // try to send verification code
    const codeSent = await ForgotPassword({ username: user.username }).catch((err: any) => {
      console.error({ action: 'onNotReceived:ForgotPassword:error', error: err });
      setError(err.message);
      return false;
    });

    setVerificationSent(codeSent);
  }

  async function onResetPassword(evt: any) {
    const { password, code } = evt;

    setError('');

    // try to reset password
    const passwordReset = await Auth.forgotPasswordSubmit(user.username, code, password).catch((err) => {
      console.error({ action: 'onResetPassword:Auth.forgotPasswordSubmit:error', error: err });
      setError(err.message);
      return false;
    });

    // returned if not verified
    if (passwordReset === false) {
      return;
    }

    // try to auto sign in
    const signedIn = await Auth.signIn(user.username, password).catch((e) => {
      console.error({ action: 'onResetPassword:Auth.signIn:error', error: e });
      setError('Could not sign in.');
      return false;
    });

    // return if could not sign in
    if (signedIn === false) {
      return;
    }

    // Route to account on successful login
    await Router.push('/dashboard').catch((e) => {
      console.error({ action: 'onResetPassword:router:error', error: e });
      setError('Could not load account page');
    });
  }

  const onResetPasswordSchema = yup.object({
    code: yup
      .string()
      .matches(/^\d{6}$/, 'Not a valid code')
      .required('Verification code is required'),
    password: yup
      .string()
      .matches(passwordRegExp, {
        message: (cur) => {
          const fails: string[] = [];
          if (!cur.value.match(/[a-z]/)) {
            fails.push('lowercase');
          }
          if (!cur.value.match(/[A-Z]/)) {
            fails.push('uppercase');
          }
          if (!cur.value.match(/[0-9]/)) {
            fails.push('number');
          }
          if (!cur.value.match(/[!@#$%^&*]/)) {
            fails.push('special character');
          }
          if (cur.value.match(/^\s/)) {
            fails.push('no spaces at the start or end');
          }
          return `Password must include ${fails.join(', ')}`;
        }
      })
      .min(8, 'Minimum length is 8 characters')
      .required('New password is required')
  });

  return (
    <>
      <div className='text-center grey-subtitle mb-4'>
        <p>Reset your password using the verification code received via email</p>
      </div>

      {sentMessage}

      <Formik
        validationSchema={onResetPasswordSchema}
        // TODO:
        // eslint-disable-next-line react/jsx-no-bind
        onSubmit={onResetPassword}
        initialValues={{
          email: user.username,
          code: '',
          password: '',
          passwordConfirm: ''
        }}
      >
        {({ isSubmitting, handleChange, handleBlur, values, touched, errors }) => (
          <Form>
            {/* Errors */}
            {error.length > 0 && (
              <Alert status='error' mb='4' borderRadius='md'>
                <AlertIcon />
                {error}
              </Alert>
            )}

            <Stack spacing={4}>
              {/* Email */}
              <FormControl
                // TODO:
                // @ts-ignore
                value={values.email}
                onChange={handleChange}
                onBlur={handleBlur}
                isInvalid={touched.email && !!errors.email}
              >
                <FormLabel>Email</FormLabel>
                <Input
                  id='email'
                  size='lg'
                  name='email'
                  type='email'
                  autoComplete='email'
                  required
                  placeholder='Email address'
                  bg={mode('white', 'gray.700')}
                  fontSize='md'
                  focusBorderColor='blue.300'
                />
                {/* @ts-ignore TODO: Fix */}
                <FormErrorMessage>{errors.email}</FormErrorMessage>
              </FormControl>

              {/* Code */}
              <FormControl
                // TODO:
                // @ts-ignore
                value={values.code}
                onChange={handleChange}
                onBlur={handleBlur}
                isInvalid={touched.code && !!errors.code}
              >
                <FormLabel>Email verification code</FormLabel>
                {/* TODO: use pin field */}
                <Input
                  id='code'
                  size='lg'
                  name='code'
                  type='text'
                  required
                  placeholder='Email verification code'
                  bg={mode('white', 'gray.700')}
                  fontSize='md'
                  focusBorderColor='blue.300'
                  autoComplete='one-time-code'
                />
                {/* @ts-ignore TODO: Fix */}
                <FormErrorMessage>{errors.code}</FormErrorMessage>
                <FormHelperText>You will receive a 6 digit code via email.</FormHelperText>
              </FormControl>

              {/* Password */}
              <FormControl
                // TODO:
                // @ts-ignore
                type='password'
                name='password'
                placeholder='Password'
                value={values.password}
                onChange={handleChange}
                onBlur={handleBlur}
                isInvalid={touched.password && !!errors.password}
              >
                <FormLabel>New Password</FormLabel>
                <InputGroup size='md'>
                  <Input
                    id='password'
                    name='password'
                    autoComplete='new-password'
                    required
                    size='lg'
                    bg={mode('white', 'gray.700')}
                    fontSize='md'
                    placeholder='New password'
                    focusBorderColor='blue.300'
                    pr='4.5rem'
                    type={showPassword ? 'text' : 'password'}
                  />
                  <InputRightElement width='4.5rem' mt='1' mr='3'>
                    <Button h='1.75rem' size='sm' onClick={toggleShowPassword}>
                      {showPassword ? 'Hide' : 'Show'}
                    </Button>
                  </InputRightElement>
                </InputGroup>
                {/* @ts-ignore TODO: Fix */}
                <FormErrorMessage>{errors.password}</FormErrorMessage>

                <FormHelperText>
                  Your password should contain the following:
                  <UnorderedList px='4' pt='2'>
                    <ListItem>minimum length of 8 characters</ListItem>
                    <ListItem>a numerical letter e.g. 123</ListItem>
                    <ListItem>a capital letter e.g. ABC</ListItem>
                    <ListItem>a lower case letter e.g. abc</ListItem>
                    <ListItem>a special character e.g. !@#$</ListItem>
                  </UnorderedList>
                </FormHelperText>
              </FormControl>
            </Stack>

            <Flex align='center' justify='space-between' mt='8'>
              {/*  TODO: */}
              {/* eslint-disable-next-line react/jsx-no-bind */}
              <Button fontSize='sm' onClick={onCancel}>
                Cancel
              </Button>
              {/*  TODO: */}
              {/* eslint-disable-next-line react/jsx-no-bind */}
              <Button fontSize='sm' onClick={onNotReceived}>
                Did not receive a verification code?
              </Button>
            </Flex>

            <LightMode>
              <Button
                size='lg'
                type='submit'
                mt='8'
                w='full'
                colorScheme='blue'
                fontSize='md'
                fontWeight='bold'
                isLoading={isSubmitting}
                isDisabled={!!errors.email || !!errors.code || !!errors.password}
              >
                Change password
              </Button>
            </LightMode>
          </Form>
        )}
      </Formik>
    </>
  );
};

export default ResetPasswordForm;
